'use client'

import { useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'
import Link from 'next/link'

/**
 * Error boundary for Hive page
 * Catches errors and displays a branded error page with recovery options
 */
export default function HiveError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Log error to Sentry
    Sentry.captureException(error)
    console.error('Hive page error:', error)
  }, [error])

  return (
    <div className='min-h-screen bg-background flex items-center justify-center px-6'>
      <div className='max-w-2xl mx-auto text-center space-y-8'>
        {/* Error Icon */}
        <div className='relative mx-auto w-24 h-24'>
          <div className='absolute inset-0 bg-destructive/10 rounded-full' />
          <div className='absolute inset-0 flex items-center justify-center'>
            <svg
              className='w-12 h-12 text-destructive'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          </div>
        </div>

        {/* Error Message */}
        <div className='space-y-4'>
          <h1 className='text-4xl font-bold text-foreground'>
            Oops! Something went wrong
          </h1>
          <p className='text-lg text-muted-foreground'>
            We encountered an error while loading Hive. Don&apos;t worry, our
            team has been notified.
          </p>
        </div>

        {/* Error Details (Development Only) */}
        {process.env.NODE_ENV === 'development' && (
          <div className='bg-destructive/10 border border-destructive/20 rounded-xl p-6 text-left'>
            <h3 className='text-sm font-semibold text-destructive mb-2'>
              Error Details (Development Only):
            </h3>
            <pre className='text-xs text-destructive/80 overflow-auto max-h-48 font-mono'>
              {error.message}
              {error.digest && `\nDigest: ${error.digest}`}
            </pre>
          </div>
        )}

        {/* Action Buttons */}
        <div className='flex flex-col sm:flex-row gap-4 justify-center pt-4'>
          <button
            onClick={reset}
            className='px-8 py-4 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
          >
            Try Again
          </button>

          <Link
            href='/vector'
            className='px-8 py-4 bg-card text-foreground font-semibold rounded-xl border-2 border-border hover:border-primary/40 transition-all duration-200 transform hover:-translate-y-0.5'
          >
            Explore Vector
          </Link>

          <Link
            href='/'
            className='px-8 py-4 bg-card text-foreground font-semibold rounded-xl border-2 border-border hover:border-primary/40 transition-all duration-200 transform hover:-translate-y-0.5'
          >
            Go Home
          </Link>
        </div>

        {/* Help Text */}
        <p className='text-sm text-muted-foreground pt-8'>
          Still having issues?{' '}
          <Link
            href='/contact-us'
            className='text-primary hover:underline font-medium'
          >
            Contact our support team
          </Link>
        </p>
      </div>
    </div>
  )
}
